(() => {
  const initialize = (root) => {
    if (!root || root.dataset.ready) return;
    let data;
    try { data = JSON.parse(root.querySelector('[data-moments-data]').textContent); } catch { return; }
    root.dataset.ready = 'true';
    const areas = {
      shirtFront: { x: .5504, y: .2074, w: .1792, h: .1152 },
      hoodieFront: { x: .5504, y: .2374, w: .1792, h: .1152 },
      mug: { x: 280 / 1086, y: 625 / 1448, w: 425 / 1086, h: 319 / 1448, curved: true },
      poster: { x: 180 / 1086, y: 205 / 1448, w: 727 / 1086, h: 985 / 1448 }
    };
    const emptyMemory = () => ({ photo: null, url: null, crop: {} });
    const state = { photo: null, photoUrl: null, memories: Array.from({ length: 4 }, emptyMemory), activeSlot: 0, slotRequests: [0, 0, 0, 0], started: false, product: null, treatment: 'photo', stage: 'choose', view: 'back', crops: {}, token: 0, photoRequest: 0, revision: 0 };
    const assets = new Map();
    const renderRuns = new WeakMap();
    const q = (s) => root.querySelector(s);
    const all = (s) => Array.from(root.querySelectorAll(s));
    const error = (message) => { q('[data-error]').textContent = message || ''; q('[data-error]').hidden = !message; };
    const status = (message) => { q('[data-status]').textContent = message || ''; };
    const defaults = () => ({ zoom: 100, x: 0, y: 0, fit: 'contain' });
    const isApparel = (key) => key === 'shirt' || key === 'hoodie';
    const cropFor = (key, slot = state.activeSlot) => {
      const crops = isApparel(key) ? state.memories[slot].crop : state.crops;
      return crops[key] || (crops[key] = defaults());
    };
    const canEditCrop = () => Boolean(isApparel(state.product) ? state.memories[state.activeSlot].photo : state.photo);
    const eventLogoUrl = typeof data.eventLogo === 'string' ? data.eventLogo.trim() : '';
    const label = (key, fallback) => data.labels[key] || fallback;
    const sideLabel = (view) => view === 'front' ? label('front_label', 'Front') : label('back_label', 'Back');
    const assetError = (message) => Object.assign(new Error(message), { code: 'DHL_ASSET' });
    const renderError = (reason) => reason?.code === 'DHL_ASSET' ? reason.message : data.labels.image_error;
    const validArea = (area) => area && ['x', 'y', 'w', 'h'].every((key) => Number.isFinite(area[key])) && area.x >= 0 && area.y >= 0 && area.w > 0 && area.h > 0 && area.x + area.w <= 1 && area.y + area.h <= 1;
    const areaFor = (key, view) => {
      const name = isApparel(key) ? `${key}${view === 'front' ? 'Front' : 'Back'}` : key;
      const area = data.areas?.[name] || areas[name];
      if (!validArea(area)) throw assetError(label('back_area_error', 'The back print placement is being prepared. Please try another keepsake.'));
      return area;
    };
    const loadImage = (url) => {
      const src = url.startsWith('//') ? `https:${url}` : url;
      if (assets.has(src)) return assets.get(src);
      const promise = new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
      assets.set(src, promise);
      promise.catch(() => assets.delete(src));
      return promise;
    };
    const sampleSources = () => {
      const seen = new Set();
      return Array.from({ length: 4 }, (_, index) => {
        const value = data.samples?.[index] || (index === 0 ? data.sample : '');
        if (typeof value !== 'string' || !value.trim() || seen.has(value.trim())) return '';
        seen.add(value.trim()); return value.trim();
      });
    };
    let examplePhotos;
    const loadExamples = () => examplePhotos || (examplePhotos = Promise.all(sampleSources().map((url) => url ? loadImage(url).catch(() => null) : Promise.resolve(null))));
    const showStage = (stage) => {
      if (stage === 'upload' && !state.product) stage = 'choose';
      if (stage === 'preview' && (!state.product || !state.started)) return;
      state.stage = stage;
      all('[data-stage]').forEach((el) => { el.hidden = el.dataset.stage !== stage; });
      all('[data-step]').forEach((el) => {
        if (el.dataset.step === stage) el.setAttribute('aria-current', 'step');
        else el.removeAttribute('aria-current');
        el.classList.toggle('is-complete', ['choose', 'upload', 'preview'].indexOf(el.dataset.step) < ['choose', 'upload', 'preview'].indexOf(stage));
      });
      q('.dhl-journey').dataset.currentStage = stage;
      const heading = q(`[data-stage="${stage}"] [data-stage-heading]`);
      heading?.focus({ preventScroll: true });
      q('.dhl-journey').scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      if (stage === 'choose') renderCards();
    };
    const drawPhoto = (context, photo, x, y, w, h, crop) => {
      context.save();
      context.beginPath();
      context.rect(x, y, w, h);
      context.clip();
      context.fillStyle = '#fff';
      context.fillRect(x, y, w, h);
      const scale = (crop.fit === 'contain' ? Math.min(w / photo.width, h / photo.height) : Math.max(w / photo.width, h / photo.height)) * crop.zoom / 100;
      const dw = photo.width * scale;
      const dh = photo.height * scale;
      const dx = x + (w - dw) * ((crop.x + 100) / 200);
      const dy = y + (h - dh) * ((crop.y + 100) / 200);
      context.drawImage(photo, dx, dy, dw, dh);
      context.restore();
    };
    const drawPendingLogo = (ctx, x, y, w, h) => {
      ctx.save();
      ctx.fillStyle = 'rgba(255,255,255,.82)';
      ctx.fillRect(x, y, w, h);
      ctx.strokeStyle = '#777168';
      ctx.lineWidth = Math.max(1, w * .009);
      ctx.setLineDash([w * .035, w * .025]);
      ctx.strokeRect(x, y, w, h);
      ctx.fillStyle = '#625d55';
      const fontSize = Math.min(w * .115, h * .17);
      ctx.font = `700 ${fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const words = label('event_logo_pending', 'EVENT LOGO PENDING').split(/\s+/);
      const lines = [];
      let line = '';
      words.forEach((word) => {
        const next = line ? `${line} ${word}` : word;
        if (line && ctx.measureText(next).width > w * .85) { lines.push(line); line = word; }
        else line = next;
      });
      if (line) lines.push(line);
      lines.forEach((text, index) => ctx.fillText(text, x + w / 2, y + h / 2 + (index - (lines.length - 1) / 2) * fontSize * 1.3, w * .9));
      ctx.restore();
    };
    const drawMemorySlot = (ctx, photo, x, y, width, height, crop, index) => {
      if (photo) { drawPhoto(ctx, photo, x, y, width, height, crop); return; }
      ctx.save();
      ctx.fillStyle = '#f2f0eb';
      ctx.fillRect(x, y, width, height);
      ctx.strokeStyle = '#b7b1a6';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 4]);
      ctx.strokeRect(x + 4, y + 4, width - 8, height - 8);
      ctx.fillStyle = '#82796b';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const size = Math.min(36, height * .2);
      ctx.font = `700 ${size}px Arial, sans-serif`;
      ctx.fillText(String(index + 1), x + width / 2, y + height * .42, width - 20);
      ctx.font = `500 ${Math.min(18, height * .115)}px Arial, sans-serif`;
      ctx.fillText(label('memory_empty', 'Add a photo'), x + width / 2, y + height * .65, width - 20);
      ctx.restore();
    };
    const drawBackDesign = (ctx, photos, width, height, crops) => {
      const scale = width / 500;
      ctx.save();
      ctx.scale(scale, height / 700);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 500, 700);
      const square = 223;
      for (let index = 0; index < 4; index += 1) {
        const x = 20 + (index % 2) * 237;
        const y = 20 + Math.floor(index / 2) * 237;
        ctx.strokeStyle = '#d40511';
        ctx.lineWidth = 2;
        ctx.strokeRect(x - 2, y - 2, square + 4, square + 4);
        drawMemorySlot(ctx, photos[index], x, y, square, square, crops[index], index);
      }
      ctx.fillStyle = '#ffcc00';
      ctx.fillRect(10, 14, 68, 7);
      ctx.fillRect(422, 477, 68, 7);
      ctx.fillStyle = '#ffcc00';
      ctx.beginPath();
      ctx.moveTo(151, 567);
      ctx.lineTo(454, 548);
      ctx.lineTo(448, 575);
      ctx.lineTo(148, 587);
      ctx.closePath();
      ctx.fill();
      ctx.save();
      ctx.translate(250, 550);
      ctx.rotate(-.065);
      ctx.fillStyle = '#24201c';
      ctx.font = 'italic 700 60px Georgia, serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(label('was_there_prefix', 'I was'), -228, 20, 150);
      ctx.fillStyle = '#d40511';
      ctx.font = 'italic 900 86px Georgia, serif';
      ctx.fillText(label('was_there_emphasis', 'there!'), -70, 22, 276);
      ctx.restore();
      ctx.fillStyle = '#d40511';
      ctx.beginPath();
      ctx.moveTo(470, 507);
      ctx.lineTo(475, 519);
      ctx.lineTo(488, 523);
      ctx.lineTo(475, 528);
      ctx.lineTo(470, 541);
      ctx.lineTo(465, 528);
      ctx.lineTo(452, 523);
      ctx.lineTo(465, 519);
      ctx.closePath();
      ctx.fill();
      const eventName = typeof data.eventName === 'string' && data.eventName.trim() ? data.eventName.trim() : label('event_name_pending', 'EVENT NAME');
      const eventDate = typeof data.eventDate === 'string' && data.eventDate.trim() ? data.eventDate.trim() : label('event_date_pending', 'EVENT DATE');
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#24201c';
      let nameSize = 27;
      do { ctx.font = `800 ${nameSize}px Arial, sans-serif`; if (ctx.measureText(eventName).width <= 446 || nameSize <= 16) break; nameSize -= 1; } while (nameSize >= 16);
      ctx.fillText(eventName, 250, 625, 446);
      ctx.fillStyle = '#655c51';
      ctx.font = '500 19px Arial, sans-serif';
      ctx.fillText(eventDate, 250, 654, 446);
      ctx.fillStyle = '#d40511';
      ctx.fillRect(219, 680, 24, 3);
      ctx.fillStyle = '#ffcc00';
      ctx.fillRect(247, 680, 34, 3);
      ctx.restore();
    };
    const render = async (canvas, key, requestedView) => {
      if (!canvas || !data.products[key]) return false;
      const example = canvas.hasAttribute('data-product-canvas');
      if (!example && !state.started) return false;
      const main = canvas.hasAttribute('data-main-canvas');
      const apparel = isApparel(key);
      const view = apparel ? (requestedView || (main ? state.view : 'back')) : '';
      const run = (renderRuns.get(canvas) || 0) + 1;
      renderRuns.set(canvas, run);
      const examples = example ? await loadExamples() : null;
      const photo = example ? examples[0] : state.photo;
      const photos = example ? examples : state.memories.map((memory) => memory.photo);
      const token = state.token;
      const revision = state.revision;
      const treatment = example ? 'photo' : state.treatment;
      const crop = example ? defaults() : { ...cropFor(key) };
      const memoryCrops = apparel ? state.memories.map((memory, index) => example ? defaults() : ({ ...cropFor(key, index) })) : [];
      const area = areaFor(key, view);
      const baseUrl = apparel && view === 'back' ? data.products[key].backImage : data.products[key].image;
      if (!baseUrl) throw assetError(label('back_asset_error', 'The back-view image is being prepared. Please try another keepsake.'));
      const base = await loadImage(baseUrl);
      let logo = null;
      if (apparel && view === 'front' && eventLogoUrl) logo = await loadImage(eventLogoUrl);
      else if (!apparel && treatment === 'frame') logo = await loadImage(data.logo);
      if ((!main && renderRuns.get(canvas) !== run) || (!example && (token !== state.token || photo !== state.photo || (main && (key !== state.product || revision !== state.revision || (apparel && view !== state.view)))))) return false;
      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(base, 0, 0, width, height);
      const x = area.x * width;
      const y = area.y * height;
      const w = area.w * width;
      const h = area.h * height;
      if (apparel && view === 'front') {
        if (logo) {
          const fontSize = w * .13;
          const gap = w * .06;
          const scale = Math.min(w / logo.width, (h - fontSize - gap) / logo.height);
          const lw = logo.width * scale;
          const lh = logo.height * scale;
          const top = y + (h - lh - gap - fontSize) / 2;
          ctx.drawImage(logo, x + (w - lw) / 2, top, lw, lh);
          ctx.save();
          ctx.fillStyle = '#171713';
          ctx.font = `700 ${fontSize}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillText(label('front_event_word', 'Event'), x + w / 2, top + lh + gap, w);
          ctx.restore();
        } else drawPendingLogo(ctx, x, y, w, h);
        canvas.dataset.rendered = key;
        canvas.dataset.renderedView = view;
        return true;
      }
      const layer = document.createElement('canvas');
      layer.width = Math.ceil(w * 2);
      layer.height = Math.ceil(h * 2);
      const art = layer.getContext('2d');
      const frame = !apparel && treatment === 'frame';
      const pad = frame ? layer.width * .025 : 0;
      const band = frame ? layer.height * .16 : 0;
      if (apparel) drawBackDesign(art, photos, layer.width, layer.height, memoryCrops);
      else {
        art.fillStyle = '#ffcc00';
        art.fillRect(0, 0, layer.width, layer.height);
        drawMemorySlot(art, photo, pad, pad, layer.width - pad * 2, layer.height - pad * 2 - band, crop, 0);
      }
      if (frame && logo) {
        const lw = layer.width * .25;
        const lh = lw * 40 / 287;
        art.drawImage(logo, pad * 2, layer.height - band / 2 - lh / 2 - pad / 2, lw, lh);
        art.fillStyle = '#24201c';
        art.font = `700 ${Math.max(8, Math.round(layer.width * .021))}px Arial`;
        art.textAlign = 'right';
        art.textBaseline = 'middle';
        art.fillText('MEMORABLE MOMENTS', layer.width - pad * 2, layer.height - band / 2 - pad / 2);
      }
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      ctx.globalAlpha = .97;
      if (area.curved) {
        const warped = document.createElement('canvas');
        warped.width = Math.ceil(w * 2);
        warped.height = Math.ceil(h * 2.08);
        const warp = warped.getContext('2d');
        const slices = 100;
        for (let i = 0; i < slices; i += 1) {
          const a = (i / slices - .5) * 1.6;
          const b = ((i + 1) / slices - .5) * 1.6;
          const dx = (Math.sin(a) / Math.sin(.8) + 1) * warped.width / 2;
          const next = (Math.sin(b) / Math.sin(.8) + 1) * warped.width / 2;
          const edge = Math.pow(Math.abs(i / slices - .5) * 2, 2);
          warp.drawImage(layer, i * layer.width / slices, 0, layer.width / slices + 1, layer.height, dx, h * .04 - edge * h * .036, next - dx + 1.5, h * 2 + edge * h * .05);
        }
        ctx.drawImage(warped, x, y - h * .02, w, h * 1.04);
      } else ctx.drawImage(layer, x, y, w, h);
      ctx.restore();
      canvas.dataset.rendered = key;
      canvas.dataset.renderedView = view;
      return true;
    };
    const renderCards = () => {
      all('[data-product-canvas]').forEach((canvas) => {
        const key = canvas.dataset.productCanvas;
        const view = isApparel(key) ? canvas.dataset.cardView || 'back' : '';
        canvas.setAttribute('aria-label', `${data.products[key].name}${view ? `: ${sideLabel(view)}` : ''} — illustrative example`);
        render(canvas, key, view).catch((reason) => error(renderError(reason)));
      });
    };
    let animation = 0;
    let mainRequest = 0;
    const renderMain = () => {
      if (!state.product) return;
      const request = ++mainRequest;
      cancelAnimationFrame(animation);
      animation = requestAnimationFrame(() => {
        const key = state.product;
        const view = isApparel(key) ? state.view : '';
        q('[data-canvas-loading]').hidden = false;
        render(q('[data-main-canvas]'), key, view).then(() => { if (request === mainRequest) q('[data-canvas-loading]').hidden = true; }).catch((reason) => {
          if (request !== mainRequest) return;
          q('[data-canvas-loading]').hidden = true;
          const canvas = q('[data-main-canvas]');
          canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
          error(renderError(reason));
        });
      });
    };
    const syncControls = () => {
      const crop = cropFor(state.product);
      const disabled = !canEditCrop();
      all('[data-crop]').forEach((input) => { input.value = crop[input.dataset.crop]; input.disabled = disabled; });
      q('[data-zoom-output]').textContent = `${crop.zoom}%`;
      q('[data-x-output]').textContent = String(crop.x);
      q('[data-y-output]').textContent = String(crop.y);
      all('input[name="dhl-fit"]').forEach((input) => { input.checked = input.value === crop.fit; input.disabled = disabled; });
      const reset = q('[data-action="reset"]');
      if (reset) reset.disabled = disabled;
    };
    const syncMemoryEditor = () => {
      const editor = q('[data-memory-editor]');
      if (editor) editor.hidden = !isApparel(state.product) || state.view === 'front';
      all('[data-slot]').forEach((button) => {
        const index = Number(button.dataset.slot);
        if (!Number.isInteger(index) || index < 0 || index > 3) return;
        button.setAttribute('aria-pressed', String(index === state.activeSlot));
        button.dataset.filled = String(Boolean(state.memories[index].photo));
        button.setAttribute('aria-label', `${label('memory', 'Memory')} ${index + 1}, ${index === 0 ? label('memory_primary', 'Main photo') : label('memory_supporting', 'Supporting photo')}, ${state.memories[index].photo ? label('edit_memory', 'Edit memory') : label('memory_empty', 'Add a photo')}`);
      });
      state.memories.forEach((memory, index) => {
        const thumb = q(`[data-slot-thumb="${index}"]`);
        if (thumb) { thumb.hidden = !memory.photo; if (memory.url) thumb.src = memory.url; else thumb.removeAttribute('src'); }
        const caption = q(`[data-slot-state="${index}"]`);
        if (caption) caption.textContent = memory.photo ? (index === state.activeSlot ? label('memory_selected', 'Selected') : label('edit_memory', 'Edit memory')) : label('memory_empty', 'Add a photo');
      });
      const title = q('[data-active-slot-title]');
      if (title) title.textContent = `${label('memory', 'Memory')} ${state.activeSlot + 1} · ${state.activeSlot === 0 ? label('memory_primary', 'Main photo') : label('memory_supporting', 'Supporting photo')}`;
      const remove = q('[data-action="remove-memory"]');
      if (remove) remove.disabled = !state.memories[state.activeSlot].photo;
    };
    const syncView = () => {
      const apparel = isApparel(state.product);
      const front = apparel && state.view === 'front';
      syncMemoryEditor();
      const primaryStrip = q('.dhl-personalise-panel .dhl-photo-strip');
      if (primaryStrip) primaryStrip.hidden = apparel;
      const intro = q('.dhl-personalise-intro');
      if (intro) intro.textContent = front ? label('front_intro', 'A small event mark, close to the heart.') : label('preview_body', 'Move the crop until your moment feels just right.');
      const views = q('[data-apparel-views]');
      if (views) views.hidden = !apparel;
      all('[data-view]').forEach((button) => button.setAttribute('aria-pressed', String(apparel && button.dataset.view === state.view)));
      const caption = q('[data-view-label]');
      if (caption) { caption.hidden = !apparel; caption.textContent = apparel ? sideLabel(state.view) : ''; }
      const controls = q('[data-photo-controls]');
      if (controls) controls.hidden = front;
      const treatment = q('.dhl-treatment');
      if (treatment) treatment.hidden = apparel;
      all('.dhl-fit, .dhl-crop').forEach((el) => { el.hidden = front; });
      const note = q('[data-apparel-note]');
      if (note) {
        note.hidden = !apparel;
        note.textContent = front
          ? (eventLogoUrl ? label('front_logo_note', 'Front: the supplied DHL logo with Event beneath, on the wearer’s left chest.') : label('front_pending_note', 'Front: your event logo will sit on the wearer’s left chest. The exact artwork has not been supplied; the marked area is a placeholder.'))
          : label('back_photo_note', 'Back: an expressive “I was there!” keepsake design with your event name and date below the photo. Fit full photo keeps everyone in view.');
      }
      const description = front ? (eventLogoUrl ? 'supplied DHL logo with Event beneath on left chest' : 'event logo artwork pending on left chest') : 'personalised concept using your selected event photo';
      q('[data-main-canvas]').setAttribute('aria-label', `${data.products[state.product].name}${apparel ? `, ${sideLabel(state.view)}` : ''}: ${description}`);
    };
    const chooseProduct = (key) => {
      if (!data.products[key]) return;
      error('');
      state.product = key;
      state.view = 'back';
      state.activeSlot = 0;
      state.revision += 1;
      q('[data-product-name]').textContent = data.products[key].name;
      all('[data-selected-product]').forEach((element) => { element.textContent = data.products[key].name; });
      const continueLabel = q('[data-continue-label]');
      if (continueLabel) continueLabel.textContent = label(`preview_${key}`, 'Preview my keepsake');
      syncControls();
      syncView();
      showStage(state.started ? 'preview' : 'upload');
      if (state.started) renderMain();
    };
    const revokeMemory = (memory) => { if (memory.url?.startsWith('blob:')) URL.revokeObjectURL(memory.url); };
    const refreshMemories = () => {
      state.photo = state.memories[0].photo;
      state.photoUrl = state.memories[0].url;
      state.token += 1;
      state.revision += 1;
      const primaryImages = [q('[data-upload-preview]'), ...all('[data-memory-thumb]')];
      primaryImages.forEach((el) => { if (state.photoUrl) el.src = state.photoUrl; else el.removeAttribute('src'); el.hidden = !state.photo; });
      q('[data-upload-empty]').hidden = Boolean(state.photo);
      q('[data-upload-ready]').hidden = !state.photo;
      q('[data-action="continue"]').hidden = !state.started;
      syncMemoryEditor();
      status(data.labels.photo_ready);
      error('');
      if (state.stage === 'choose') renderCards();
      else if (state.stage === 'preview' && state.product) { syncControls(); syncView(); renderMain(); }
    };
    const usePhoto = (image, url) => {
      state.memories.forEach(revokeMemory);
      state.memories = Array.from({ length: 4 }, emptyMemory);
      state.memories[0] = { photo: image, url, crop: {} };
      state.activeSlot = 0;
      state.started = true;
      state.crops = {};
      refreshMemories();
    };
    const replaceMemory = (index, image, url) => {
      revokeMemory(state.memories[index]);
      state.memories[index] = { photo: image, url, crop: {} };
      state.started = true;
      if (index === 0) state.crops = {};
      refreshMemories();
    };
    const invalidateSlots = () => { state.slotRequests = state.slotRequests.map((request) => request + 1); };
    const readFile = (file) => {
      if (!file) return;
      const request = ++state.photoRequest;
      invalidateSlots();
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 20 * 1024 * 1024 || !file.size) { error(data.labels.upload_error); return; }
      status(data.labels.loading);
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        if (request !== state.photoRequest) { URL.revokeObjectURL(url); return; }
        if (image.width > 15000 || image.height > 15000 || image.width * image.height > 80000000) { URL.revokeObjectURL(url); error(data.labels.decode_error); return; }
        usePhoto(image, url);
      };
      image.onerror = () => { URL.revokeObjectURL(url); if (request === state.photoRequest) error(data.labels.decode_error); };
      image.src = url;
    };
    const readSlotFile = (file, index) => {
      if (!file || !Number.isInteger(index) || index < 0 || index > 3) return;
      state.photoRequest += 1;
      const request = ++state.slotRequests[index];
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 20 * 1024 * 1024 || !file.size) { error(data.labels.upload_error); return; }
      status(data.labels.loading);
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        if (request !== state.slotRequests[index]) { URL.revokeObjectURL(url); return; }
        if (image.width > 15000 || image.height > 15000 || image.width * image.height > 80000000) { URL.revokeObjectURL(url); error(data.labels.decode_error); return; }
        replaceMemory(index, image, url);
      };
      image.onerror = () => { URL.revokeObjectURL(url); if (request === state.slotRequests[index]) error(data.labels.decode_error); };
      image.src = url;
    };
    q('#DhlMomentPhoto').addEventListener('change', (event) => { readFile(event.target.files[0]); event.target.value = ''; });
    const slotInput = q('#DhlSlotPhoto');
    let slotPickerTarget = 0;
    if (slotInput) {
      slotInput.addEventListener('click', () => { slotPickerTarget = state.activeSlot; });
      slotInput.addEventListener('change', (event) => { readSlotFile(event.target.files[0], slotPickerTarget); event.target.value = ''; });
    }
    const drop = q('[data-drop-zone]');
    ['dragenter', 'dragover'].forEach((name) => drop.addEventListener(name, (event) => { event.preventDefault(); drop.classList.add('is-dragging'); }));
    ['dragleave', 'drop'].forEach((name) => drop.addEventListener(name, (event) => { event.preventDefault(); drop.classList.remove('is-dragging'); }));
    drop.addEventListener('drop', (event) => readFile(event.dataTransfer.files[0]));
    root.addEventListener('click', async (event) => {
      const cardToggle = event.target.closest('[data-card-toggle]');
      if (cardToggle) {
        const card = cardToggle.closest('[data-product-card]');
        const view = cardToggle.dataset.cardToggle;
        if (card && ['front', 'back'].includes(view)) {
          card.dataset.activeView = view;
          card.querySelectorAll('[data-card-toggle]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.cardToggle === view)));
        }
        return;
      }
      const choose = event.target.closest('[data-choose]');
      if (choose) { chooseProduct(choose.dataset.choose); return; }
      const slot = event.target.closest('[data-slot]');
      if (slot && isApparel(state.product) && state.view === 'back') {
        const index = Number(slot.dataset.slot);
        if (!Number.isInteger(index) || index < 0 || index > 3) return;
        state.activeSlot = index;
        syncMemoryEditor(); syncControls();
        if (!state.memories[index].photo && slotInput) { slotPickerTarget = index; slotInput.click(); }
        return;
      }
      const view = event.target.closest('[data-view]')?.dataset.view;
      if (view && isApparel(state.product) && ['front', 'back'].includes(view)) {
        if (state.view !== view) { state.view = view; state.revision += 1; error(''); syncView(); renderMain(); }
        return;
      }
      if (event.target.closest('[data-start-link]')) { showStage('choose'); return; }
      const action = event.target.closest('[data-action]')?.dataset.action;
      if (!action) return;
      if (['upload', 'choose'].includes(action)) { error(''); showStage(action); return; }
      if (action === 'continue') { if (state.product && state.started) { syncControls(); syncView(); showStage('preview'); renderMain(); } return; }
      if (action === 'sample') {
        error(''); status(data.labels.loading);
        const request = ++state.photoRequest;
        invalidateSlots();
        if (!state.product) { showStage('choose'); return; }
        const sources = sampleSources();
        const photos = await Promise.all(sources.map((url) => url ? loadImage(url).catch(() => null) : Promise.resolve(null)));
        if (request !== state.photoRequest) return;
        if (!photos.some(Boolean)) { error(data.labels.sample_error); return; }
        state.memories.forEach(revokeMemory);
        state.memories = photos.map((photo, index) => ({ photo, url: photo ? sources[index] : null, crop: {} }));
        state.started = true; state.activeSlot = 0; state.crops = {};
        refreshMemories();
        if (state.stage === 'choose') return;
        syncControls(); syncView(); showStage('preview'); renderMain();
      }
      if (action === 'remove-memory' && isApparel(state.product) && state.view === 'back') {
        const index = state.activeSlot;
        state.photoRequest += 1; state.slotRequests[index] += 1;
        revokeMemory(state.memories[index]); state.memories[index] = emptyMemory();
        if (index === 0) state.crops = {};
        refreshMemories();
        status(label('memory_removed', 'Memory removed. You can add another photo to this slot.'));
        return;
      }
      if (action === 'reset' && state.product && canEditCrop()) {
        const crops = isApparel(state.product) ? state.memories[state.activeSlot].crop : state.crops;
        crops[state.product] = defaults(); state.revision += 1; syncControls(); renderMain();
      }
      if (action === 'save' && state.product) {
        error('');
        const key = state.product;
        const token = state.token;
        const revision = state.revision;
        const photoRequest = state.photoRequest;
        const view = isApparel(key) ? state.view : '';
        const button = q('[data-action="save"]');
        button.disabled = true;
        try {
          const rendered = await render(q('[data-main-canvas]'), key, view);
          if (!rendered || token !== state.token || revision !== state.revision || photoRequest !== state.photoRequest || key !== state.product || (view && view !== state.view)) return;
          const blob = await new Promise((resolve) => q('[data-main-canvas]').toBlob(resolve, 'image/jpeg', .94));
          if (token !== state.token || revision !== state.revision || photoRequest !== state.photoRequest || key !== state.product || (view && view !== state.view)) return;
          if (!blob) { error(data.labels.save_error); return; }
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `DHL-Memorable-Moments-${key}${view ? `-${view}` : ''}-concept.jpg`;
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 30000);
          status(data.labels.save_success);
        } catch { error(data.labels.save_error); }
        finally { button.disabled = false; }
      }
    });
    root.addEventListener('input', (event) => {
      if (event.target.matches('[data-crop]') && state.product && canEditCrop()) {
        cropFor(state.product)[event.target.dataset.crop] = Number(event.target.value);
        state.revision += 1;
        syncControls(); renderMain();
      }
    });
    root.addEventListener('change', (event) => {
      if (event.target.matches('input[name="dhl-treatment"]') && !isApparel(state.product)) { state.treatment = event.target.value; state.revision += 1; renderMain(); }
      if (event.target.matches('input[name="dhl-fit"]') && state.product && canEditCrop()) { cropFor(state.product).fit = event.target.value; state.revision += 1; renderMain(); }
    });
    document.querySelectorAll('[data-nav-reset]').forEach((link) => link.addEventListener('click', () => showStage('choose')));
    q('.dhl-journey').dataset.currentStage = 'choose';
    renderCards();
  };
  document.querySelectorAll('[data-dhl-moments]').forEach(initialize);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target.querySelector('[data-dhl-moments]')));
})();
